import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import * as React from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "@/lib/firebase";
import {
  getSavedAccounts,
  removeAccount,
  type SavedAccount,
} from "@/lib/auth-storage";
import {
  signInWithFirebase,
  ApiError,
  getPostAuthRedirect,
} from "@/lib/api/auth";
import { formatRelativeTimeFull } from "@/lib/formatting";
import { LoginPasswordStep } from "@/components/auth/login-password-step";
import { LoginEmailStep } from "@/components/auth/login-email-step";
import { LoginEmailPasswordStep } from "@/components/auth/login-email-password-step";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Icon } from "@/components/ui/icons";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { Heading } from "@/components/ui/heading";
import { Separator } from "@/components/ui/separator";

type NavState =
  | { view: "options" }
  | { view: "password"; account: SavedAccount }
  | { view: "email" }
  | { view: "emailPassword"; email: string };

function LoginPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string };
  const redirectTo = search.redirect ?? null;
  const { t: tAuth } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");

  const [nav, setNav] = React.useState<NavState>({ view: "options" });
  const [savedAccounts, setSavedAccounts] = React.useState<SavedAccount[]>([]);

  React.useEffect(() => {
    setSavedAccounts(getSavedAccounts());
  }, []);

  const handleAccountSelect = (account: SavedAccount) => {
    setNav({ view: "password", account });
  };

  const handleRemoveAccount = (e: React.MouseEvent, accountId: string) => {
    e.stopPropagation();
    removeAccount(accountId);
    setSavedAccounts(getSavedAccounts());
  };

  // Google sign-in is a mutation, not a query — useMutation owns the
  // {loading, error} pair without a separate useState. Errors translate
  // to a displayable message in JSX; popup-closed surfaces as
  // `googleError === ""` (suppressed, since dismissal is user intent).
  const googleSignIn = useMutation({
    mutationFn: async () => {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const email = result.user.email;
      if (!email) {
        throw new Error("No email returned from Google sign-in");
      }
      return signInWithFirebase({ id_token: idToken, email });
    },
    onSuccess: (user) => {
      void navigate({ to: (redirectTo || getPostAuthRedirect(user)) as never });
    },
    onError: (err) => {
      console.error("Google sign-in error:", err);
    },
  });

  const googleError = (() => {
    const err = googleSignIn.error;
    if (!err) return "";
    if (err instanceof ApiError) {
      if (err.code === "INVALID_USERNAME") return tAuth("accountNotFound");
      return err.message;
    }
    if (err instanceof Error) {
      // Suppress firebase popup-closed-by-user — it's a user cancellation,
      // not an error to surface.
      if (err.message.includes("popup-closed")) return "";
      return err.message;
    }
    return tCommon("error");
  })();

  const handleGoogleSignIn = () => {
    googleSignIn.mutate();
  };

  const goBack = () => {
    setNav({ view: "options" });
  };

  const handleSuccess = (path: string) => {
    void navigate({ to: path as never });
  };

  if (nav.view === "password") {
    return (
      <LoginPasswordStep
        account={nav.account}
        redirectTo={redirectTo}
        onSuccess={handleSuccess}
        onBack={goBack}
      />
    );
  }

  if (nav.view === "email") {
    return (
      <LoginEmailStep
        redirectTo={redirectTo}
        onSuccess={handleSuccess}
        onEmailVerified={(email) => {
          setNav({ view: "emailPassword", email });
        }}
        onBack={goBack}
      />
    );
  }

  if (nav.view === "emailPassword") {
    return (
      <LoginEmailPasswordStep
        email={nav.email}
        redirectTo={redirectTo}
        onSuccess={handleSuccess}
        onBack={goBack}
      />
    );
  }

  return (
    <VStack gap="none">
      <Heading size="xl" className="mb-2">
        {savedAccounts.length > 0 ? tAuth("welcomeBack") : tAuth("signIn")}
      </Heading>
      <Text color="muted" className="mb-6">
        {savedAccounts.length > 0 ? tAuth("orSignInWith") : tAuth("enterEmail")}
      </Text>

      {savedAccounts.length > 0 && (
        <VStack gap="sm" className="mb-6">
          <Text weight="medium" className="mb-1">
            {tAuth("fastSignIn")}
          </Text>
          {savedAccounts.map((account) => (
            <HStack
              key={account.id}
              onClick={() => handleAccountSelect(account)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                e.key === "Enter" && handleAccountSelect(account)
              }
              // eslint-disable-next-line design-tokens/no-token-classname-override -- interactive card with hover border/bg states
              className="w-full p-3 border border-[color:var(--outline-variant)] rounded-lg hover:bg-[color:var(--background)] hover:border-[color:var(--primary)] transition-all group cursor-pointer"
            >
              <Avatar
                size="md"
                src={account.avatarUrl}
                name={account.displayName}
              />

              <VStack gap="none" className="ml-3 text-left flex-1 min-w-0">
                <Text weight="medium" truncate>
                  {account.displayName}
                </Text>
                <Text color="muted" truncate>
                  {account.email}
                </Text>
              </VStack>

              <HStack className="ml-2">
                <Text size="xs" color="muted" className="hidden sm:block">
                  {formatRelativeTimeFull(account.lastLoginAt, tCommon)}
                </Text>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleRemoveAccount(e, account.id)}
                  aria-label={tCommon("remove")}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Icon.ChevronLeft size="sm" />
                </IconButton>
              </HStack>
            </HStack>
          ))}
        </VStack>
      )}

      {savedAccounts.length > 0 && (
        <HStack className="relative mb-6">
          <Separator className="flex-1" />
          <Text color="muted" size="sm" className="px-2">
            {tCommon("or")}
          </Text>
          <Separator className="flex-1" />
        </HStack>
      )}

      <VStack>
        <Button
          color="neutral"
          variant="outline"
          size="lg"
          fullWidth
          onClick={handleGoogleSignIn}
          disabled={googleSignIn.isPending}
        >
          <Icon.Google className="w-5 h-5 mr-3" />
          {tAuth("continueWithGoogle")}
        </Button>

        <Button
          color="neutral"
          variant="outline"
          size="lg"
          fullWidth
          onClick={() => setNav({ view: "email" })}
          disabled={googleSignIn.isPending}
          data-testid="continue-with-email-btn"
        >
          <Icon.Email size="md" color="muted" className="mr-3" />
          {tAuth("continueWithEmail")}
        </Button>
      </VStack>

      {googleError ? (
        <Text color="danger" className="mt-4" align="center">
          {googleError}
        </Text>
      ) : null}

      <Text color="muted" className="mt-6" align="center">
        {tAuth("dontHaveAccount")}{" "}
        <Link
          to="/register"
          className="text-[color:var(--primary)] hover:opacity-80 font-medium"
        >
          {tAuth("signUp")}
        </Link>
      </Text>
    </VStack>
  );
}

export const Route = createFileRoute("/_auth/login")({
  component: LoginPage,
});
